
import React from 'react';
import { EnhancedCTraderProfileCard } from '@/components/accounts/EnhancedCTraderProfileCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CTraderAccount } from '@/components/accounts/ctrader-card/types';
import { useIsMobile } from '@/hooks/use-mobile';

interface EnhancedProfilesGridProps {
  isLoading: boolean;
  profiles: CTraderAccount[] | undefined;
  onDisconnect: (id: string) => Promise<void>;
  onReconnect: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onCreateProfile?: () => void;
  searchQuery?: string;
}

export const EnhancedProfilesGrid = ({
  isLoading,
  profiles,
  onDisconnect,
  onReconnect,
  onDelete,
  onCreateProfile,
  searchQuery
}: EnhancedProfilesGridProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const gap = isMobile ? 'gap-3' : 'gap-4 md:gap-6';
  const gridCols = isMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2';

  const LoadingCard = () => (
    <Card className="overflow-hidden border border-border/50 shadow-sm">
      <div className={`${isMobile ? 'p-3' : 'p-4 md:p-6'} space-y-4`}>
        <div className="flex items-center gap-3">
          <Skeleton className={`${isMobile ? 'h-10 w-10' : 'h-12 w-12'} rounded-xl`} />
          <div className="space-y-2 flex-1">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-24" />
          </div>
          <Skeleton className="h-6 w-20 rounded-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
        <div className="flex justify-between">
          <Skeleton className={`${isMobile ? 'h-7 w-20' : 'h-8 w-24'} rounded-lg`} />
          <Skeleton className={`${isMobile ? 'h-7 w-7' : 'h-8 w-8'} rounded-lg`} />
        </div>
      </div>
    </Card>
  );

  const EmptyStateCard = ({ isSearch = false }: { isSearch?: boolean }) => (
    <Card className="col-span-full border-dashed border-2 border-muted bg-gradient-to-br from-muted/10 to-muted/5 overflow-hidden">
      <div className="relative flex flex-col items-center justify-center py-12 md:py-16 px-4 md:px-6 text-center">
        {/* Background decoration */}
        <div className="absolute top-4 right-4 w-20 h-20 bg-primary/5 rounded-full blur-xl" />
        <div className="absolute bottom-4 left-4 w-16 h-16 bg-blue-500/5 rounded-full blur-xl" />
        
        <div className="relative">
          <div className={`${isMobile ? 'mb-4 p-4' : 'mb-6 p-6'} bg-gradient-to-br from-primary/10 to-primary/5 rounded-full`}>
            <Users className={`${isMobile ? 'h-8 w-8' : 'h-12 w-12'} text-primary`} />
          </div>
          <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-foreground mb-2 md:mb-3`}>
            {isSearch ? t('no_trading_accounts_search') : 'No CTrader Profiles Yet'}
          </h3>
          <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground ${isMobile ? 'mb-6' : 'mb-8'} max-w-md leading-relaxed`}>
            {isSearch 
              ? `No profiles match your search for "${searchQuery}". Try adjusting your search criteria.`
              : "Connect your first CTrader profile to start managing your trading connections and monitor your activities."
            }
          </p>
          {!isSearch && onCreateProfile && (
            <Button 
              onClick={onCreateProfile}
              size={isMobile ? "sm" : "default"}
              className="group bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02]"
            >
              <Plus className={`${isMobile ? 'h-4 w-4 mr-1' : 'h-5 w-5 mr-2'} transition-transform duration-200 group-hover:rotate-90`} />
              Create Your First Profile
            </Button>
          )}
        </div>
      </div>
    </Card>
  );

  if (isLoading) {
    return (
      <div className={`grid ${gridCols} ${gap}`}>
        {Array.from({ length: 4 }).map((_, index) => (
          <div 
            key={index}
            className="animate-fade-in"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <LoadingCard />
          </div>
        ))}
      </div>
    );
  }

  if (!profiles?.length) {
    return <EmptyStateCard isSearch={!!searchQuery} />;
  }

  return (
    <div className={`grid ${gridCols} ${gap}`}>
      {profiles.map((account, index) => (
        <div 
          key={account.id}
          className="animate-fade-in"
          style={{ animationDelay: `${index * 100}ms` }}
        >
          <EnhancedCTraderProfileCard
            account={account}
            onDisconnect={onDisconnect}
            onReconnect={onReconnect}
            onDelete={onDelete}
          />
        </div>
      ))}
    </div>
  );
};
